import { useRef, useState, useCallback } from 'react';

interface TiltOptions {
  /** Maximum tilt angle in degrees (default 12) */
  maxTilt?: number;
  /** How fast the card follows mouse on move (default 0.06s) */
  followSpeed?: number;
  /** How fast the card resets on leave (default 0.6s) */
  resetSpeed?: number;
  /** Scale factor while hovered (default 1.025) */
  scale?: number;
}

interface TiltResult {
  cardRef: React.RefObject<HTMLDivElement | null>;
  shineRef: React.RefObject<HTMLDivElement | null>;
  isHovered: boolean;
  handlers: {
    onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseLeave: () => void;
  };
}

/**
 * useTilt — adds a GPU-accelerated 3D tilt effect that follows the cursor.
 * Uses direct DOM mutation (no React state) for the transform so it stays
 * at 60fps without triggering re-renders on every mouse move.
 */
export function useTilt({
  maxTilt    = 12,
  followSpeed = 0.06,
  resetSpeed  = 0.6,
  scale      = 1.025,
}: TiltOptions = {}): TiltResult {
  const cardRef  = useRef<HTMLDivElement>(null);
  const shineRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = cardRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;   // px from left edge
      const y = e.clientY - rect.top;    // px from top edge

      // Normalize to [-0.5, 0.5]
      const nx = x / rect.width  - 0.5;
      const ny = y / rect.height - 0.5;

      // rotateY  = tilt left/right   (+nx = lean right)
      // rotateX  = tilt up/down      (-ny = lean up when cursor is high)
      const rotY =  nx * maxTilt * 2;
      const rotX = -ny * maxTilt * 2;

      el.style.transition = `transform ${followSpeed}s ease`;
      el.style.transform  = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale3d(${scale},${scale},${scale})`;

      // Move specular shine to cursor position
      if (shineRef.current) {
        const pctX = (x / rect.width)  * 100;
        const pctY = (y / rect.height) * 100;
        shineRef.current.style.background =
          `radial-gradient(circle at ${pctX}% ${pctY}%, rgba(255,255,255,0.13) 0%, transparent 58%)`;
        shineRef.current.style.opacity = '1';
      }
    },
    [maxTilt, followSpeed, scale]
  );

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);

    const el = cardRef.current;
    if (el) {
      el.style.transition = `transform ${resetSpeed}s cubic-bezier(0.23, 1, 0.32, 1)`;
      el.style.transform  = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    }
    if (shineRef.current) {
      shineRef.current.style.opacity = '0';
    }
  }, [resetSpeed]);

  return {
    cardRef,
    shineRef,
    isHovered,
    handlers: { onMouseMove, onMouseEnter, onMouseLeave },
  };
}
